import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { useSelector, useDispatch } from 'react-redux';
import Moment from 'moment';

import { addDateTime } from '../../../../common/date-time';
import { getReseLeakMonitoringTableDataInit } from '../redux/actions';
import ReseLeakDetectionMonitoringInfoDialog from './ReseLeakDetectionMonitoringInfoDialog';
import './ReseMonitoringRequestLogs.scss';

const ReseMonitoringRequestLogs = () => {
    const [tableFilters, setTableFilters] = useState(null);
    const [dialogData, setDialogData] = useState(null);
    const [logsRange, setLogsRange] = useState([addDateTime("day", -14), addDateTime("day", 0)]);
    const monitoringTableRef = useRef(null);
    const dispatch = useDispatch();

    const monitoringTableData = useSelector((state) => state.reseLeakDetectionMonitoring.reseLeakMonitoringTableData);

    useEffect(() => {
        fetchMonitoringTableData();
        initializeTableFilters();
    }, []);

    const fetchMonitoringTableData = (range = logsRange) => {
        const startDatetime = Moment(range[0]).format("YYYY-MM-DD");
        const endDatetime = !!range[1] ? Moment(range[1]).format("YYYY-MM-DD") : Moment(range[0]).format("YYYY-MM-DD");
        const offset = Moment(new Date()).utcOffset();
        dispatch(getReseLeakMonitoringTableDataInit(startDatetime, endDatetime, offset));
    };

    const initializeTableFilters = () => {
        setTableFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            division: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
            },
            storeId: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
            },
            assetName: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
            },
            materialType: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
            },
            monitoringDuration: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
            },
        });
    };

    const onGetLogs = () => {
        fetchMonitoringTableData(logsRange);
    };

    const chargeSizeTemplate = ({ chargeSize }) => (chargeSize === null || chargeSize === undefined ? "" : chargeSize);
    const monitoringStartDateTemplate = ({ monitoringStartDate }) => monitoringStartDate || "";
    const monitoringDurationTemplate = ({ monitoringDuration }) => (monitoringDuration === null || monitoringDuration === undefined ? "" : monitoringDuration);

    const actionsTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-info-circle"
                    className="p-button-text"
                    onClick={() => setDialogData(rowData)}
                />
            </div>
        );
    };

    return (
        <div className="rese-monitoring-request-logs-container">
            <ReseLeakDetectionMonitoringInfoDialog dialogData={dialogData} setDialogData={setDialogData} />
            <div className="logs-container-top">
                <div className="row">
                    <div className="col-12 col-lg-6 input-field">
                        <label>Logs Range</label>
                        <Calendar
                            name="logsRange"
                            value={logsRange}
                            onChange={(e) => setLogsRange(e.value)}
                            selectionMode="range"
                            maxDate={addDateTime("month", 0)}
                            showIcon
                        />
                    </div>
                </div>
                <div className="row get-logs-buttons">
                    <div className="col-12">
                        <Button label="Get Logs" className="p-button-primary" onClick={onGetLogs} />
                    </div>
                </div>
            </div>
            <div className="d-flex justify-content-end">
                <Button
                    name='refresh'
                    className='p-button-text'
                    icon="pi pi-refresh"
                    onClick={() => fetchMonitoringTableData()}
                />
                <Button
                    name='download'
                    className='p-button-text'
                    icon="pi pi-download"
                    onClick={() => monitoringTableRef.current?.exportCSV()}
                />
            </div>
            <DataTable
                value={monitoringTableData}
                ref={monitoringTableRef}
                showGridlines
                filters={tableFilters}
                filterDisplay='menu'
                onFilter={(e) => setTableFilters(e.filters)}
                sortField="modifiedTimestamp"
                sortOrder={-1}
                rows={10}
                rowsPerPageOptions={[10, 20, 50]}
                paginator
                paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                emptyMessage="No monitoring records found."
            >
                <Column field="division" header="Division" sortable filter />
                <Column field="storeId" header="Store ID" sortable filter />
                <Column field="assetName" header="Asset Name" sortable filter />
                <Column field="materialType" header="Material Type" sortable filter />
                <Column field="chargeSize" header="Charge Size" body={chargeSizeTemplate} sortable />
                <Column field="monitoringStartDate" header="Monitoring Start Date" body={monitoringStartDateTemplate} sortable />
                <Column field="monitoringDuration" header="Monitoring Duration (Days)" body={monitoringDurationTemplate} sortable filter />
                <Column header="Actions" body={actionsTemplate} />
            </DataTable>
        </div>
    );
};

export default ReseMonitoringRequestLogs;
